import { useTranslation } from 'react-i18next';
import { Link, useLocation, useMatch, useNavigate, useSearchParams } from 'react-router-dom';

import { Button, Checkbox, Tag, message, notification } from 'antd';
import {
  type BreadcrumbItemType,
  type BreadcrumbSeparatorType,
} from 'antd/lib/breadcrumb/Breadcrumb';
import clsx from 'clsx';
import {
  type Key,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TruncatedTooltip from '../components/Common/TruncatedTooltip';
import { Icon } from '../components/Core/Icon';
import { iconComponents } from '../components/Core/IconImports';
import { EventBusEventEnum } from '../enums/EventBusEventEnum';
import { FileMenuActionEnum } from '../enums/FileMenuActionEnum';
import { FileOrderColumnEnum } from '../enums/FilesOrderColumnEnum';
import { FilesTableEnum } from '../enums/FilesTableEnum';
import type File from '../interfaces/File';
import type FileAction from '../interfaces/FileAction';
import type { GetFilesPayload } from '../interfaces/FileService';
import type TableEmptyState from '../interfaces/TableEmptyState';
import type User from '../interfaces/User';
import { type FileActionKey, filteredFileActions } from '../lib/fileActions';
import EventBus from '../services/EventBus';
import { FileService } from '../services/fileService';
import { getContrastBackgroundColorForText } from '../utils/colorsUtilis';
import { useAuth } from './useAuth';
import { useFileDownload } from './useFileDownload';
import useFilesActions from './useFilesActions';
import { useFileUpload } from './useFileUpload';
import useOpenFile from './useOpenFile';
import { usePermissions } from './usePermissions';
import { useRightPanel } from './useRightPanel';
import useTableHeight from './useTableHeight';
import { useTags } from './useTags';

interface Props {
  table: FilesTableEnum;
  folderUid?: string;
}

/**
 * Returns the partial filters based on the table.
 * @param {FilesTableEnum} table - The table to get the partial filters for.
 * @returns {GetFilesPayload} The partial filters.
 */
const getPartialFiltersBasedOnTable = (table: FilesTableEnum) => {
  switch (table) {
    case FilesTableEnum.Personal:
      return {
        list: 0,
        type: 2,
      };
    case FilesTableEnum.Received:
      return {
        list: 0,
        type: 1,
      };
    case FilesTableEnum.Shared:
      return {
        list: 4,
        type: 2,
      };
    case FilesTableEnum.Favorites:
      return {
        list: 2,
        type: 2,
      };
    case FilesTableEnum.Hidden:
      return {
        list: 3,
        type: 2,
      };
    case FilesTableEnum.Trash:
      return {
        list: 5,
        type: 2,
      };
    default:
      return {
        list: 0,
        type: 2,
      };
  }
};

const fileService = new FileService();

function useFiles({ table, folderUid }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { onNewFileUploaded, offNewFileUploaded, setCurrentFolderUid } = useFileUpload();
  const { authenticatedUser, isReader } = useAuth();
  const { copyFileLinkToClipboard } = useOpenFile();
  const { tags, tagsBasedOnUser, isTagDisabledForFile } = useTags();
  const { isRightPanelOpen, openRightPanel } = useRightPanel();
  const [isContextMenuVisible, setIsContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState<{
    x: number;
    y: number;
    direction: 'up' | 'down';
  }>({
    x: 0,
    y: 0,
    direction: 'down',
  });
  const lastManualTableScrollRef = useRef<number>(0);
  const pendingTableScrollTopTimeoutRef = useRef<number | null>(null);

  const {
    isOnBehalfOfUser,
    onBehalfOfUser,
    isAdminOrContributor,
    handleOnBehalfOfUserChange,
    onBehalfOfUserChanged,
    offBehalfOfUserChanged,
    isInFileRoute,
  } = useAuth();
  const { openFileSafely } = useOpenFile();
  const { handleDownloadFiles } = useFileDownload();
  const { selectedTags } = useTags();
  const {
    handleCreateFolder,
    handleCreateFileFromTemplate,
    handleDownloadEncryptedFile,
    handleRenameFile,
    handleFavouriteFileChange,
    handleCreateFileShortcut,
    handleCreateFilesDuplicate,
    handleReplaceFile,
    handleMoveFiles,
    handleDeleteFiles,
    handleRestoreFilesFromTrash,
    handlePermanentlyDeleteFiles,
    handleHideFiles,
    handleUnhideFiles,
    handleShareFiles,
    handleUpdateTags,
    handleOpenShortcutOriginalFileLocation,
    handleOpenFileFromTrash,
    handleSellFile,
    handleEmptyTrash,
    handleAiClassifyFile,
    handleAiSummaryFile,
    handleAiDocumentChatFile,
  } = useFilesActions({
    folderUid,
  });
  const { tableScrollY } = useTableHeight();

  const { setIsCurrentFolderReadOnly, isCurrentFolderReadOnly } = usePermissions();
  // Get query params
  const [searchParams] = useSearchParams();
  const prependFileId = searchParams.get('prependFileId');
  const prependFileIdNum = prependFileId ? parseInt(prependFileId) : null;
  const [prependFileData, setPrependFileData] = useState<File | null>(null);

  const [selectedTableKeys, setSelectedTableKeys] = useState<Key[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [parentFolder, setParentFolder] = useState<File | null>(null);
  const [totalFiles, setTotalFiles] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<GetFilesPayload>({
    offset: 0,
    count: 50,
    orderColumn: FileOrderColumnEnum.Title,
    orderAsc: true,
    onBehalfOf: onBehalfOfUser?.id,
    list: folderUid ? 0 : getPartialFiltersBasedOnTable(table).list,
    type: getPartialFiltersBasedOnTable(table).type,
    parent: folderUid,
  });
  const rowSelection = {
    selectedRowKeys: selectedTableKeys,
    onChange: (newSelectedKeys: Key[]) => {
      setSelectedTableKeys(newSelectedKeys);
    },
  };
  const [isFilesListInGridMode, setIsFilesListInGridMode] = useState(() => {
    const storedValue = sessionStorage.getItem('isFilesListInGridMode');
    return storedValue === 'true';
  });
  const matchFilesSection = useMatch('/files/:section/*');
  const currentSection = matchFilesSection?.params?.section || 'personal';
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  /**
   * Function to add new files to the list of files
   * @param newFiles - The new files to add
   */
  const onFilesCreated = (newFiles: File[]) => {
    // Add only files that have the parent the same as the parentFolder
    const filteredFiles = newFiles.filter(
      (file) =>
        file.parentId === parentFolder?.id || (file.parentId === null && parentFolder === null),
    );

    setFiles((prevFiles) => [...filteredFiles, ...prevFiles]);
    setTotalFiles(totalFiles + filteredFiles.length);
  };

  /**
   * Function to update the list of files
   * @param updatedFiles - The updated files
   */
  const onFilesUpdated = (updatedFiles: File[]) => {
    setFiles((prevFiles) =>
      prevFiles.map((file) => updatedFiles.find((f) => f.id === file.id) || file),
    );
  };

  /**
   * Function to remove files from the list of files
   * @param removedFiles - The removed files
   */
  const onFilesDeleted = (removedFiles: File[]) => {
    setFiles((prevFiles) => prevFiles.filter((file) => !removedFiles.includes(file)));
    // Fix pagination
    setTotalFiles(totalFiles - removedFiles.length);
    setSelectedTableKeys([]);
  };

  /**
   * Handles the row context menu event.
   *
   * This function is triggered when a context menu event is invoked on a row. It prevents
   * the default browser context menu, updates the selected user, sets the position for
   * the context menu based on the user's cursor, and makes the context menu visible.
   *
   * @param {Group} record - The group record associated with the row where the context menu was triggered.
   * @param {React.MouseEvent} event - The React mouse event object for the context menu interaction.
   */
  const handleRowContextMenu = (record: File, event: MouseEvent) => {
    event.preventDefault();

    // If the record.uid is is not already in the selectedTableKeys, add it
    if (!selectedTableKeys.includes(record.id)) {
      setSelectedTableKeys([record.id]);
    }

    const menuWidth = 220;
    // sometimes the menu ism't initialized, so I calculate its height and add the header's height.
    let menuHeight = contextMenuFileActions.length * 32 + 24;
    const contextMenu = document.getElementsByClassName('file-context-menu')[0];
    if (contextMenu) {
      menuHeight = contextMenu.clientHeight;
    }
    let direction: 'up' | 'down' = 'down';

    // Final position variables
    let left: number;
    let top: number;

    // If the click is on the right half of the screen, position the menu to the left of the pointer;
    // otherwise, position it to the right.
    if (event.clientX > window.innerWidth / 2) {
      left = event.clientX - menuWidth;
    } else {
      left = event.clientX;
    }

    // If the click is in the lower half of the screen, position the menu above the pointer;
    // otherwise, position it below.
    if (event.clientY > window.innerHeight / 2) {
      top = event.clientY - menuHeight;
      direction = 'up';
    } else {
      top = event.clientY;
    }

    // Security corrections to not go out of the screen
    if (left < 10) left = 10;
    if (left + menuWidth > window.innerWidth) {
      left = window.innerWidth - menuWidth - 10;
    }
    if (top < 10) top = 10;
    if (top + menuHeight > window.innerHeight) {
      top = window.innerHeight - menuHeight - 10;
    }

    setContextMenuPosition({ x: left, y: top, direction });
    setIsContextMenuVisible(true);
  };

  /**
   * Handles the click event on a file row.
   *
   * This function checks if the user has clicked on the filename of a file or folder.
   * If the user has clicked on the filename of a folder, the function navigates to the folder.
   * If the user has clicked on the filename of a file, the function opens the file.
   * If the user has clicked on the row, the function opens the context menu.
   *
   * @param record - The file record associated with the row that was clicked.
   * @param hasClickedOnFilename - A boolean indicating if the user has clicked on the filename of a file or folder.
   */
  const handleFileRowClick = (
    record: File,
    hasClickedOnFilename: boolean,
    event?: React.MouseEvent,
  ) => {
    if (hasClickedOnFilename && record.isFolder) {
      //Empty the selectedTableKeys
      setSelectedTableKeys([]);
      navigate(`/files/${currentSection}/folder/${record.id}`);
    } else if (hasClickedOnFilename && !isInFileRoute(FilesTableEnum.Trash)) {
      openFileSafely(record);
    } else if (hasClickedOnFilename && isInFileRoute(FilesTableEnum.Trash)) {
      handleOpenFileFromTrash(record, {
        onFilesDeleted: onFilesDeleted,
      });
    } else {
      handleRowClick(record, event);
    }
  };

  /**
   * Handles the double click event on a file row.
   *
   * This function checks if the user has double clicked on the filename of a file or folder.
   * If the user has double clicked on the filename of a folder, the function navigates to the folder.
   * If the user has double clicked on the filename of a file, the function opens the file.
   */
  const handleFileRowDoubleClick = (record: File) => {
    if (record.isFolder) {
      navigate(`/files/${currentSection}/folder/${record.id}`);
    } else {
      if (!isInFileRoute(FilesTableEnum.Trash)) {
        openFileSafely(record);
      } else {
        handleOpenFileFromTrash(record, {
          onFilesDeleted: onFilesDeleted,
        });
      }
    }
  };

  /**
   * Handles the file menu action.
   * @param fileIds - The file IDs.
   * @param action - The action to perform.
   */
  const handleFileMenuAction = (fileIds: number[], action: FileActionKey) => {
    const foundFile = files.find((file) => file.id === fileIds[0]);

    switch (action) {
      case FileMenuActionEnum.Open:
        if (fileIds.length === 1 && foundFile) {
          // Check if is a folder
          if (foundFile.isFolder) {
            navigate(`/files/${currentSection}/folder/${foundFile.id}`);
          } else {
            if (!isInFileRoute(FilesTableEnum.Trash)) {
              openFileSafely(foundFile);
            } else {
              handleOpenFileFromTrash(foundFile, {
                onFilesDeleted: onFilesDeleted,
              });
            }
          }
        }
        break;
      case FileMenuActionEnum.CopyLink:
        if (fileIds.length === 1 && foundFile) {
          copyFileLinkToClipboard(foundFile);
          message.success(t('common.linkCopied'));
        }
        break;
      case FileMenuActionEnum.OpenShortcutOriginalFileLocation:
        if (fileIds.length === 1 && foundFile) {
          handleOpenShortcutOriginalFileLocation(foundFile);
        }
        break;
      case FileMenuActionEnum.Download:
        handleDownloadFiles(fileIds, foundFile?.fileName);
        break;
      case FileMenuActionEnum.DownloadEncrypted:
        if (fileIds.length === 1 && foundFile) {
          handleDownloadEncryptedFile(foundFile);
        }
        break;
      case FileMenuActionEnum.Rename:
        if (fileIds.length === 1 && foundFile) {
          handleRenameFile(foundFile, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;
      case FileMenuActionEnum.AddToFavorites: {
        const foundFiles = files.filter((file) => fileIds.includes(file.id));
        if (foundFiles.length > 0) {
          handleFavouriteFileChange(foundFiles, true, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;
      }
      case FileMenuActionEnum.RemoveFromFavorites: {
        const foundFiles = files.filter((file) => fileIds.includes(file.id));
        if (foundFiles.length > 0) {
          handleFavouriteFileChange(foundFiles, false, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;
      }
      case FileMenuActionEnum.CreateShortcut:
        if (fileIds.length === 1 && foundFile) {
          handleCreateFileShortcut(foundFile, {
            onFilesCreated: onFilesCreated,
          });
        }
        break;
      case FileMenuActionEnum.Duplicate:
        const foundFiles = files.filter((file) => fileIds.includes(file.id));
        if (foundFiles.length > 0) {
          handleCreateFilesDuplicate(foundFiles, {
            onFilesCreated: onFilesCreated,
          });
        }
        break;
      case FileMenuActionEnum.Replace:
        if (fileIds.length === 1 && foundFile) {
          handleReplaceFile(foundFile, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;
      case FileMenuActionEnum.Move:
        {
          const foundFiles = files.filter((file) => fileIds.includes(file.id));
          handleMoveFiles(foundFiles, parentFolder, getPartialFiltersBasedOnTable(table), {
            onFilesDeleted: onFilesDeleted,
          });
        }
        break;
      case FileMenuActionEnum.Hide:
        {
          const foundFiles = files.filter((file) => fileIds.includes(file.id));
          handleHideFiles(foundFiles, {
            onFilesDeleted: onFilesDeleted,
          });
        }
        break;
      case FileMenuActionEnum.Unhide:
        {
          const foundFiles = files.filter((file) => fileIds.includes(file.id));
          handleUnhideFiles(foundFiles, {
            onFilesDeleted: onFilesDeleted,
          });
        }
        break;
      case FileMenuActionEnum.Delete:
        {
          const foundFiles = files.filter((file) => fileIds.includes(file.id));
          handleDeleteFiles(foundFiles, {
            onFilesDeleted: onFilesDeleted,
          });
        }
        break;
      case FileMenuActionEnum.DeletePermanently:
        {
          const foundFiles = files.filter((file) => fileIds.includes(file.id));
          handlePermanentlyDeleteFiles(foundFiles, {
            onFilesDeleted: onFilesDeleted,
          });
        }
        break;
      case FileMenuActionEnum.RestoreFromTrash:
        {
          const foundFiles = files.filter((file) => fileIds.includes(file.id));
          handleRestoreFilesFromTrash(foundFiles, {
            onFilesDeleted: onFilesDeleted,
          });
        }
        break;
      case FileMenuActionEnum.Share:
        {
          const foundFiles = files.filter((file) => fileIds.includes(file.id));
          handleShareFiles(foundFiles, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;
      case FileMenuActionEnum.SaveTags:
        {
          if (fileIds.length === 1 && foundFile) {
            handleUpdateTags(foundFile, selectedTagIds || [], {
              onFilesUpdated: onFilesUpdated,
            });
          }
        }
        break;
      case FileMenuActionEnum.SellFile:
        if (fileIds.length === 1 && foundFile) {
          handleSellFile(foundFile, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;
      case FileMenuActionEnum.Classify:
        if (fileIds.length === 1 && foundFile) {
          handleAiClassifyFile(foundFile, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;
      case FileMenuActionEnum.ArtificalIntelligenceSummary:
        if (fileIds.length === 1 && foundFile) {
          handleAiSummaryFile(foundFile, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;
      case FileMenuActionEnum.ArtificalIntelligenceDocumentChat:
        if (fileIds.length === 1 && foundFile) {
          handleAiDocumentChatFile(foundFile, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;
      case FileMenuActionEnum.Details:
        openRightPanel();
        break;
      case FileMenuActionEnum.EmptyTrash:
        handleEmptyTrash({
          onFilesDeleted: () => {
            setFiles([]);
            setTotalFiles(0);
            setSelectedTableKeys([]);
          },
        });
        break;
    }
  };

  /**
   * Handles the click event on a table row.
   *
   * When invoked, this function performs the following actions:
   * 1. Sets the selected user based on the clicked row's associated record.
   * 2. Opens a right-hand panel for displaying or editing the selected user's details.
   *
   * @param {User} record - The data record associated with the row that was clicked.
   */
  const handleRowClick = (record: File, event?: React.MouseEvent) => {
    const isShiftPressed = event?.shiftKey;
    const isCtrlPressed = event?.ctrlKey || event?.metaKey; // cmd su Mac
    let selectedRows: number[] = [];

    if (isShiftPressed && rowSelection.selectedRowKeys.length === 1) {
      const fileSelectedIndex = files.findIndex(
        (item) => rowSelection.selectedRowKeys[0] === item.id,
      );
      const fileClickedIndex = files.findIndex((item) => item.id === record.id);

      if (fileClickedIndex > fileSelectedIndex) {
        selectedRows = files.slice(fileSelectedIndex, fileClickedIndex + 1).map((f) => f.id);
      } else if (fileClickedIndex < fileSelectedIndex) {
        selectedRows = files.slice(fileClickedIndex, fileSelectedIndex + 1).map((f) => f.id);
      }
    } else if (isCtrlPressed) {
      if (rowSelection.selectedRowKeys.includes(record.id)) {
        selectedRows = rowSelection.selectedRowKeys
          .filter((key) => key !== record.id)
          .map((i) => parseInt(i as string));
      } else {
        selectedRows = [
          ...rowSelection.selectedRowKeys.map((i) => parseInt(i as string)),
          record.id,
        ];
      }
    } else {
      selectedRows = [record.id];
    }

    setSelectedTableKeys(selectedRows);
    setIsContextMenuVisible(false);
  };

  /**
   * Handles the change event on the table.
   *
   * This function updates the filters based on the table's sorting criteria.
   * It updates the orderColumn and orderAsc properties in the filters object
   * based on the sorting field and order.
   *
   * @param {any} sorter - The sorting parameters from the table.
   */
  const handleTableChange = (_: any, __: any, sorter?: any) => {
    let orderColumn = filters.orderColumn;
    let orderAsc = filters.orderAsc;

    if (sorter && sorter.field) {
      switch (sorter.field) {
        case 'title':
          orderColumn = FileOrderColumnEnum.Title;
          break;
        case 'fileSize':
          orderColumn = FileOrderColumnEnum.Size;
          break;
        case 'lastModified':
        case 'deletedAt':
          orderColumn = FileOrderColumnEnum.ModifiedAt;
          break;
        default:
          break;
      }
      orderAsc = sorter.order === 'ascend';
    }

    handleFiltersChange({
      parent: folderUid,
      list: folderUid ? 0 : filters.list,
      offset: 0,
      orderColumn,
      orderAsc,
    });
  };

  /**
   * Generates breadcrumb items based on the current table and parent folder.
   *
   * This function creates an array of breadcrumb items that represent the current path
   * in the file navigation hierarchy. It includes the current table and all parent folders
   * up to the root.
   */
  const breadcrumbItems = useMemo(() => {
    let currentHref = `/files/${currentSection}`;

    let currentTitle = <Link to={currentHref}>{t('common.files')}</Link>;
    let currentTitleWithoutLink = t('common.files');
    switch (table) {
      case FilesTableEnum.Personal:
        currentTitle = <Link to={currentHref}>{t('common.files')}</Link>;
        currentTitleWithoutLink = t('common.files');
        break;
      case FilesTableEnum.Received:
        currentTitle = <Link to={currentHref}>{t('common.received')}</Link>;
        currentTitleWithoutLink = t('common.received');
        break;
      case FilesTableEnum.Shared:
        currentTitle = <Link to={currentHref}>{t('common.shared')}</Link>;
        currentTitleWithoutLink = t('common.shared');
        break;
      case FilesTableEnum.Favorites:
        currentTitle = <Link to={currentHref}>{t('common.favorites')}</Link>;
        currentTitleWithoutLink = t('common.favorites');
        break;
      case FilesTableEnum.Hidden:
        currentTitle = <Link to={currentHref}>{t('common.hidden')}</Link>;
        currentTitleWithoutLink = t('common.hidden');
        break;
      case FilesTableEnum.Trash:
        currentTitle = <Link to={currentHref}>{t('common.trash')}</Link>;
        currentTitleWithoutLink = t('common.trash');
        break;
      default:
    }

    const items: Partial<BreadcrumbItemType & BreadcrumbSeparatorType>[] = [
      {
        href: parentFolder?.path && parentFolder.path.length > 0 ? currentHref : undefined,
        title:
          parentFolder?.path && parentFolder.path.length > 0 ? (
            currentTitle
          ) : (
            <span>{currentTitleWithoutLink}</span>
          ),
      },
    ];

    if (parentFolder) {
      parentFolder.path?.map((folder) => {
        // Check if is the last element
        const isLastElement = folder.uid === parentFolder.id;

        items.push({
          href: isLastElement ? undefined : `${currentHref}/${folder.uid}`,
          title: !isLastElement ? (
            <Link to={`${currentHref}/folder/${folder.uid}`}>{folder.title}</Link>
          ) : (
            <span>{folder.title}</span>
          ),
        });
      });
    }

    return items;
  }, [t, table, parentFolder]);

  /**
   * Updates the filters state to load more items by incrementing the offset value.
   * The offset is increased by the count value in the previous state.
   * This function is typically used to fetch the next set of data in a paginated list.
   */
  const handleLoadMore = () => {
    if (hasMore) {
      setFilters((prev) => ({
        ...prev,
        offset: prev.offset + prev.count,
      }));
    }
  };

  /**
   * Handles the change event on the filters.
   *
   * This function updates the filters state based on the provided filters.
   * It also removes the prependFileData if it exists and updates the URL.
   *
   * @param filters - The new filters to apply.
   */
  const handleFiltersChange = (filters: Partial<GetFilesPayload>) => {
    setFilters((prev) => ({
      ...prev,
      ...filters,
    }));

    // If prependFileIdNum is not null, remove it and remove it from the url
    if (prependFileIdNum) {
      setPrependFileData(null);

      // Remove prependFileId from the query string
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('prependFileId');
      navigate(
        {
          pathname: location.pathname,
          search: newParams.toString(),
        },
        { replace: true },
      );
    }
  };

  /**
   * selectedFiles - A memoized array of file objects that have been filtered based on
   * matching file IDs with the provided `selectedTableKeys`.
   *
   * This variable uses React's `useMemo` hook to optimize performance by ensuring the
   * filtering operation is only re-executed when either `files` or `selectedTableKeys`
   * values change. The result is a filtered array of files whose `uid` is included in
   * the `selectedTableKeys` array.
   *
   * Dependencies:
   * - `files`: An array of file objects, where each file object contains a `uid` property.
   * - `selectedTableKeys`: An array of file IDs to be matched against the `files` array.
   */
  const selectedFiles = useMemo(() => {
    return files.filter((file) => selectedTableKeys.includes(file.id));
  }, [files, selectedTableKeys]);

  /**
   * Determines if the dropzone feature should be disabled based on a set of conditions.
   *
   * The dropzone is disabled in the following cases:
   * - When the file list is of type 'all':
   *   - If `filters.type` is 1 and the folder is not writable.
   *   - If `filters.type` is not 1 and the user is neither an admin nor a contributor.
   * - When the file list is of types 'shared', 'favorites', or 'archived':
   *   - If `filters.folder.id` is not defined.
   *   - If `filters.folder.id` is defined and the folder is not writable.
   * - For any other file list type.
   *
   */
  const isUploadDisabledInCurrentFolder = useMemo(() => {
    switch (table) {
      case FilesTableEnum.Personal:
      case FilesTableEnum.Received:
        if (filters.type === 1) {
          return !isCurrentFolderReadOnly;
        } else {
          return !isAdminOrContributor;
        }
      case FilesTableEnum.Shared:
      case FilesTableEnum.Favorites:
      case FilesTableEnum.Hidden:
        // Only if inside a folder
        return !folderUid || !parentFolder || parentFolder.isReadOnly;

      default:
        return true;
    }
  }, [filters, isCurrentFolderReadOnly, table]);

  useEffect(() => {
    EventBus.emit(EventBusEventEnum.IsUploadDisabledInCurrentFolder, {
      isDisabled: isUploadDisabledInCurrentFolder,
    });
  }, [isUploadDisabledInCurrentFolder, isCurrentFolderReadOnly, table]);

  /**
   * Memoized filters
   *
   * This is used to prevent unnecessary re-renders of the component when the filters change
   */
  const memoizedFilters = useMemo<GetFilesPayload>(() => {
    return {
      ...filters,
    };
  }, [
    filters.offset,
    filters.parent,
    filters.onBehalfOf,
    filters.fileTypes,
    filters.shareWith,
    filters.sharedFrom,
    filters.tags,
    filters.orderAsc,
    filters.orderColumn,
    filters.count,
    filters.list,
    filters.onlySigned,
    filters.onlyUnsealed,
  ]);

  /**
   * Empty state for the table
   */
  const emptyState: TableEmptyState = useMemo(() => {
    let iconName: string;
    switch (currentSection) {
      case 'personal':
        iconName = 'FolderIcon';
        break;
      case 'received':
        iconName = 'FileDownIcon';
        break;
      case 'shared':
        iconName = 'FileUserIcon';
        break;
      case 'favorites':
        iconName = 'StarIcon';
        break;
      case 'hidden':
        iconName = 'ArchiveIcon';
        break;
      case 'trash':
        iconName = 'TrashIcon';
        break;
      default:
        iconName = 'FilesIcon';
    }

    let label: string;
    if (isLoading) {
      label = t('common.loadingFiles');
    } else if (folderUid) {
      label = t('common.noFilesInThisFolder');
    } else {
      switch (currentSection) {
        case 'personal':
          label = t('common.dropHereYourFirstFile');
          break;
        case 'received':
          label = t('common.noFilesReceived');
          break;
        case 'shared':
          label = t('common.hereYouCanFindSharedFiles');
          break;
        case 'favorites':
          label = t('common.hereYouCanFindFavoritesFiles');
          break;
        case 'hidden':
          label = t('common.hereYouCanFindHiddenFiles');
          break;
        case 'trash':
          label = t('common.hereYouCanFindDeletedFiles');
          break;
        default:
          label = t('common.noFiles');
      }
    }

    return { label, iconName: iconName as keyof typeof iconComponents };
  }, [currentSection, isLoading, t, folderUid, isUploadDisabledInCurrentFolder]);

  /**
   * Context for the files actions
   */
  const filesActionsContext = useMemo(
    () => ({
      files: selectedFiles || [],
      authenticatedUser,
      isInFileRoute,
      isAdminOrContributor,
      isReader,
      isCurrentFolderReadOnly,
      tags,
      isRightPanelOpen,
      isUploadDisabledInCurrentFolder,
    }),
    [
      selectedFiles,
      authenticatedUser,
      isInFileRoute,
      isAdminOrContributor,
      isReader,
      isCurrentFolderReadOnly,
      tags,
      isRightPanelOpen,
      isUploadDisabledInCurrentFolder,
    ],
  );

  /**
   * First selected file
   */
  const firstSelectedFile = useMemo(() => selectedFiles?.[0], [selectedFiles]);

  /**
   * Has first selected file tag changes
   */
  const hasFirstSelectedFileTagChanges = useMemo(() => {
    const original: number[] = firstSelectedFile?.tags?.map((tag) => tag.id) || [];
    const selected: number[] = selectedTagIds;

    if (original.length !== selected.length) return true;

    return !original.every((id) => selected.includes(id));
  }, [firstSelectedFile?.tags, selectedTagIds]);

  /**
   * Build context menu file actions with tags
   */
  const buildFileActionsWithTags = useCallback(
    (baseActions: FileAction[]) => {
      return baseActions.map((action) => {
        if (action.key === FileMenuActionEnum.Tags) {
          const tagsItem = tagsBasedOnUser.map((tag) => ({
            key: `tag-${tag.id}`,
            label: (
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={selectedTagIds.includes(tag.id)}
                  onChange={(event) => {
                    if (isTagDisabledForFile(firstSelectedFile!, tag)) return;

                    if (event.target.checked) {
                      setSelectedTagIds([...selectedTagIds, tag.id]);
                    } else {
                      setSelectedTagIds(selectedTagIds.filter((id) => id !== tag.id));
                    }
                  }}
                />
                <Tag
                  key={tag.id}
                  style={{
                    backgroundColor: getContrastBackgroundColorForText(tag?.color),
                  }}
                  className={clsx(
                    'relative w-full transition-all duration-300',
                    !tag.fileVisible &&
                    'pointer-events-none absolute left-0 top-0 w-full bg-[repeating-linear-gradient(-45deg,transparent,transparent_5px,#ccc_5px,#ccc_10px)] opacity-50 mix-blend-multiply',
                    isTagDisabledForFile(firstSelectedFile!, tag) &&
                    'pointer-events-none opacity-50',
                  )}
                  onClick={() => {
                    if (isTagDisabledForFile(firstSelectedFile!, tag)) return;
                    if (selectedTagIds.includes(tag.id)) {
                      setSelectedTagIds(selectedTagIds.filter((id) => id !== tag.id));
                    } else {
                      setSelectedTagIds([...selectedTagIds, tag.id]);
                    }
                  }}
                >
                  <div className="flex w-full flex-row items-center justify-center lg:justify-between">
                    <div />
                    <span
                      className="hidden max-w-28 text-center lg:block"
                      style={{
                        color: tag.color,
                      }}
                    >
                      <TruncatedTooltip>{tag.title}</TruncatedTooltip>
                    </span>
                    <span
                      style={{
                        color: tag.color,
                      }}
                      className="lg:hidden"
                    >
                      <TruncatedTooltip>{tag.title}</TruncatedTooltip>
                    </span>
                    <div className="hidden lg:block">
                      {tag.publicTag ? (
                        <Icon name="BuildingIcon" className="h-4 w-4 text-gray-dark" />
                      ) : (
                        <div className="w-1" />
                      )}
                    </div>
                  </div>
                </Tag>
              </div>
            ),
            icon: null,
          }));

          return {
            ...action,
            type: 'submenu',
            children: [
              ...tagsItem,
              {
                type: 'divider',
              },
              {
                key: FileMenuActionEnum.SaveTags,
                label: (
                  <Button
                    type="link"
                    size="small"
                    className="w-full text-center"
                    disabled={!hasFirstSelectedFileTagChanges}
                  >
                    {t('common.applyTags')}
                  </Button>
                ),

                icon: null,
                type: 'item',
              },
            ],
          } as FileAction;
        }
        return action;
      });
    },
    [
      t,
      tagsBasedOnUser,
      firstSelectedFile,
      isTagDisabledForFile,
      selectedTagIds,
      setSelectedTagIds,
    ],
  );

  /**
   * Context menu file actions
   */
  const contextMenuFileActions = useMemo(() => {
    const actions = filteredFileActions(filesActionsContext);
    return buildFileActionsWithTags(actions);
  }, [filesActionsContext, buildFileActionsWithTags]);

  /**
   * Top bar file actions
   */
  const topBarFileActions = useMemo(
    () => buildFileActionsWithTags(filteredFileActions(filesActionsContext, false, true)),
    [filesActionsContext, buildFileActionsWithTags],
  );

  /**
   * Get the new files uploaded and add them to the list of files.
   */
  useEffect(() => {
    // Check if files can be added to the current folder
    const checkIfFileCanBeAdded = (file: File, isReplacing: boolean) => {
      // If is replacing and file is not in the list, return false
      if (isReplacing && !files.some((f) => f.id === file.id)) {
        return false;
      }

      // If the file has no parentId and openFolders is empty and the file is not in the list and we are in the personal files route, return true
      if (
        !file.parentId &&
        !parentFolder &&
        !files.some((f) => f.id === file.id) &&
        isInFileRoute(FilesTableEnum.Personal)
      ) {
        return true;
      }

      // If the file has a parentId and the last folder in openFolders is the parent of the file and the file is not in the list, return true
      if (file.parentId === parentFolder?.id && !files.some((f) => f.id === file.id)) {
        return true;
      }
      return false;
    };

    /**
     * Scroll to top when idle
     */
    const scrollToTopWhenIdle = () => {
      const body = document.querySelector<HTMLElement>('.ant-table-body');
      if (!body) return;

      const now = Date.now();
      const sinceLastScroll = now - lastManualTableScrollRef.current;
      if (sinceLastScroll >= 2000) {
        body.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        if (pendingTableScrollTopTimeoutRef.current)
          clearTimeout(pendingTableScrollTopTimeoutRef.current);
        pendingTableScrollTopTimeoutRef.current = window.setTimeout(() => {
          body.scrollTo({ top: 0, behavior: 'smooth' });
        }, 2000 - sinceLastScroll);
      }
    };

    const handleNewFileUploaded = (file: File, isReplacing: boolean) => {
      // Add the file to the list of files if it's not already there and if it's not a duplicate and if it's not already being uploaded and if its parent is the current folder
      if (checkIfFileCanBeAdded(file, isReplacing)) {
        // If is replacing and the file is in the list, replace it
        if (isReplacing && files.some((f) => f.id === file.id)) {
          setFiles((prevFiles) => prevFiles.map((f) => (f.id === file.id ? { ...file } : f)));
        } else {
          setFiles((prevFiles) => [file, ...prevFiles]);
          setTotalFiles((prevCount) => prevCount + 1);
          scrollToTopWhenIdle();
        }
      }
    };

    // Subscribe to the event
    onNewFileUploaded(handleNewFileUploaded);

    // Clear the event listener once the component is unmounted
    return () => {
      offNewFileUploaded(handleNewFileUploaded);
      if (pendingTableScrollTopTimeoutRef.current) {
        clearTimeout(pendingTableScrollTopTimeoutRef.current);
      }
    };
  }, [onNewFileUploaded, offNewFileUploaded]);

  useEffect(() => {
    if (firstSelectedFile?.tags?.length && firstSelectedFile.tags.length > 0) {
      setSelectedTagIds(firstSelectedFile.tags.map((tag) => tag.id));
    } else {
      setSelectedTagIds([]);
    }
  }, [firstSelectedFile?.tags]);

  /**
   * Handles the "on behalf of user" state change.
   *
   * This effect is used to update the filters when the "on behalf of user" state changes.
   * It also navigates to the files page with the new filters.
   */
  useEffect(() => {
    const handler = (user: User | null) => {
      handleFiltersChange({
        parent: undefined,
        list: getPartialFiltersBasedOnTable(table).list,
        onBehalfOf: user?.id,
        offset: 0,
      });
      navigate(`/files/${table}`);
    };

    onBehalfOfUserChanged(handler);

    return () => {
      offBehalfOfUserChanged(handler);
    };
  }, [onBehalfOfUserChanged, offBehalfOfUserChanged]);

  /**
   * Get the files
   */
  useEffect(() => {
    setIsLoading(true);
    fileService
      .getFiles(memoizedFilters)
      .then((response) => {
        if (response.error) {
          return;
        }
        setFiles((prevFiles) => {
          // Avoid duplicates by id
          const allFiles =
            memoizedFilters.offset === 0 ? response.myFiles : [...prevFiles, ...response.myFiles];

          const filesById = new Map();
          allFiles.forEach((f) => filesById.set(f.id, f));
          return Array.from(filesById.values());
        });
        setTotalFiles(response.total);
        setParentFolder(response.parent);

        if (response.parent?.isReadOnly === false) {
          setIsCurrentFolderReadOnly(true);
        } else {
          setIsCurrentFolderReadOnly(false);
        }
        setHasMore(response.myFiles.length === memoizedFilters.count);
      })
      .catch(() => {
        notification.error({
          message: t('errors.genericFetchError', { resource: t('common.files') }),
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [memoizedFilters]);

  /**
   * Prepend file
   */
  useEffect(() => {
    if (!prependFileIdNum || isNaN(prependFileIdNum)) {
      setPrependFileData(null);
      return;
    }
    // Search the file in the list
    const found = files.find((f) => f.id === prependFileIdNum);
    if (found) {
      // Move it to the top and set isUnRead: true
      setFiles((prevFiles) => {
        // If it's already in the top and has isUnRead true, do nothing
        if (prevFiles[0]?.id === prependFileIdNum && prevFiles[0]?.isUnRead) return prevFiles;
        // Remove the file from the current position
        const filtered = prevFiles.filter((f) => f.id !== prependFileIdNum);
        // Insert in top with isUnRead: true
        return [{ ...found, isUnRead: true }, ...filtered];
      });
      setPrependFileData(null);
    } else {
      // Not present: fetch only that file and prepend
      fileService
        .getFiles({ ...memoizedFilters, ids: [prependFileIdNum.toString()] })
        .then((resp) => {
          const prependFile = resp.myFiles?.[0];
          if (prependFile) {
            setPrependFileData({ ...prependFile, isUnRead: true });
          }
        });
    }
    // eslint-disable-next-line
  }, [files, prependFileIdNum]);

  /**
   * Prepend file data
   */
  useEffect(() => {
    if (prependFileData) {
      setFiles((prevFiles) => {
        // Check if it's already present
        //const alreadyPresent = prevFiles.some((f) => f.id === prependFileData.id);
        // Filter out any duplicate
        const filtered = prevFiles.filter((f) => f.id !== prependFileData.id);
        // Prepend
        return [prependFileData, ...filtered];
      });

      if (memoizedFilters.offset === 0) {
        // Check if it was already present before updating the count
        setTotalFiles((prevCount) => {
          // If it was already present, the count remains the same, otherwise it increments
          const alreadyPresent = files.some((f) => f.id === prependFileData.id);
          return alreadyPresent ? prevCount : prevCount + 1;
        });

        // Fix hasMore:
        // If the list now has more elements than count (page size), there is still "hasMore".
        // Attention: here you can also want to leave it true if the backend has others,
        // but typically with client-side pagination you need to consider if the page is now "overflowed".
        setHasMore(() => {
          // If you are showing exactly "count" elements and you have added one more,
          // the page is full as before
          const nextFilesLength =
            files.length + (files.some((f) => f.id === prependFileData.id) ? 0 : 1);
          return nextFilesLength >= filters.count;
        });
      }

      setPrependFileData(null);
    }
  }, [prependFileData, memoizedFilters.offset, filters.count, files]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      list: folderUid ? 0 : getPartialFiltersBasedOnTable(table).list,
      parent: folderUid,
      offset: 0,
    }));
  }, [folderUid]);

  useEffect(() => {
    const newTagIds = selectedTags.map((tag) => tag.id).sort();
    const oldTagIds = (filters.tags || []).slice().sort();

    const areEqual =
      newTagIds.length === oldTagIds.length && newTagIds.every((id, idx) => id === oldTagIds[idx]);

    if (!areEqual) {
      setFilters((prev) => ({
        ...prev,
        tags: newTagIds,
        offset: 0,
      }));
    }
  }, [selectedTags, filters.tags]);

  useEffect(() => {
    sessionStorage.setItem('isFilesListInGridMode', isFilesListInGridMode.toString());
  }, [isFilesListInGridMode]);

  useEffect(() => {
    if (parentFolder?.id) {
      setCurrentFolderUid(parentFolder.id);
    } else {
      setCurrentFolderUid(null);
    }
  }, [parentFolder]);

  useEffect(() => {
    const handler = () => {
      handleCreateFolder({
        onFilesCreated: onFilesCreated,
      });
    };
    EventBus.on(EventBusEventEnum.CreateFolder, handler);
    return () => {
      EventBus.off(EventBusEventEnum.CreateFolder, handler);
    };
  }, [handleCreateFolder, parentFolder]);

  useEffect(() => {
    const handler = ({ template }: { template: 'word' | 'excel' | 'powerpoint' }) => {
      handleCreateFileFromTemplate(template, {
        onFilesCreated: onFilesCreated,
      });
    };
    EventBus.on(EventBusEventEnum.CreateFileFromTemplate, handler);
    return () => {
      EventBus.off(EventBusEventEnum.CreateFileFromTemplate, handler);
    };
  }, [handleCreateFileFromTemplate, parentFolder]);

  useEffect(() => {
    const handler = ({ files }: { files: File[] }) => {
      onFilesUpdated(files);
    };
    EventBus.on(EventBusEventEnum.OnFilesUpdated, handler);
    return () => {
      EventBus.off(EventBusEventEnum.OnFilesUpdated, handler);
    };
  }, [onFilesUpdated, parentFolder]);

  useEffect(() => {
    const handler = ({ files }: { files: File[] }) => {
      onFilesDeleted(files);
    };
    EventBus.on(EventBusEventEnum.OnFilesDeleted, handler);
    return () => {
      EventBus.off(EventBusEventEnum.OnFilesDeleted, handler);
    };
  }, [onFilesDeleted, parentFolder]);

  useEffect(() => {
    setSelectedTableKeys([]);
  }, [location.pathname]);

  useEffect(() => {
    const body = document.querySelector<HTMLElement>('.ant-table-body');
    if (!body) return;

    let hideTimeout: number | null = null;

    const show = () => {
      body.classList.remove('scroll-hidden');
      body.classList.add('scroll-visible');
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }
    };
    const hide = () => {
      hideTimeout = window.setTimeout(() => {
        body.classList.remove('scroll-visible');
        body.classList.add('scroll-hidden');
      }, 100);
    };

    body.classList.add('scroll-hidden');
    body.addEventListener('mouseenter', show);
    body.addEventListener('scroll', show, { passive: true });
    body.addEventListener('mouseleave', hide);

    // Track last manual scroll
    const handleManualScroll = () => {
      lastManualTableScrollRef.current = Date.now();
      if (pendingTableScrollTopTimeoutRef.current) {
        clearTimeout(pendingTableScrollTopTimeoutRef.current);
        pendingTableScrollTopTimeoutRef.current = null;
      }
    };
    body.addEventListener('scroll', handleManualScroll);

    return () => {
      body.removeEventListener('mouseenter', show);
      body.removeEventListener('scroll', show);
      body.removeEventListener('mouseleave', hide);
      body.removeEventListener('scroll', handleManualScroll);
      if (hideTimeout) clearTimeout(hideTimeout);
      if (pendingTableScrollTopTimeoutRef.current)
        clearTimeout(pendingTableScrollTopTimeoutRef.current);
    };
  }, [tableScrollY]);

  return {
    isOnBehalfOfUser,
    onBehalfOfUser,
    handleOnBehalfOfUserChange,
    files,
    totalFiles,
    hasMore,
    isLoading,
    filters,
    setFilters,
    rowSelection,
    selectedTableKeys,
    setSelectedTableKeys,
    handleRowContextMenu,
    handleTableChange,
    handleRowClick,
    handleLoadMore,
    breadcrumbItems,
    isFilesListInGridMode,
    setIsFilesListInGridMode,
    isContextMenuVisible,
    setIsContextMenuVisible,
    contextMenuPosition,
    setContextMenuPosition,
    handleFileMenuAction,
    selectedFiles,
    handleFileRowClick,
    handleFileRowDoubleClick,
    isUploadDisabledInCurrentFolder,
    emptyState,
    contextMenuFileActions,
    topBarFileActions,
    handleFiltersChange,
    tableScrollY,
  };
}

export default useFiles;
